<script>
 import {
        MDBModal,
        MDBModalHeader,
        MDBModalTitle,
        MDBModalBody,
        MDBCollapse,
        MDBBtn
    } from 'mdb-vue-ui-kit';
  import { reactive, defineComponent } from 'vue';
  import MonthNav from '../components/MonthNav.vue';
  import BudgetItemGrid from '../components/BudgetItemGrid.vue';
  import CategoryGrid from '@/components/CategoryGrid.vue';
  import SankeyChart from '@/components/SankeyChart.vue';
  import DailySpendChart from '@/components/DailySpendChart.vue';
  import CumulativeSpendChart from '@/components/CumulativeSpendChart.vue';
  import { checkLogin } from '../common/auth';
  import { get, post, patch } from '../common/api';

  const CATEGORY_ICONS = {
    gym_memberships: 'fa-dumbbell',
    rent: 'fa-home',
    daily_travel: 'fa-subway',
    health_and_fitness: 'fa-heartbeat',
    car_payment: 'fa-car',
    utilities: 'fa-lightbulb',
    steep_hollow: 'fa-tree',
    subscriptions: 'fa-podcast',
    zogchow: 'fa-utensils',
    car_insurance: 'fa-shield-alt',
    clothing: 'fa-tshirt',
    discretionary: 'fa-store',
    car_maintenance: 'fa-wrench',
    other: 'fa-folder',
    entertainment: 'fa-mountain',
    groceries: 'fa-carrot',
  };

  const TYPE_COLORS = {
    expense: '#ef4444',
    income: '#10b981',
    investment: '#8b5cf6',
    transfer: '#f59e0b',
    cc_payment: '#3b82f6',
  };

  export default defineComponent({
    name: "MonthView",
    components: {
      BudgetItemGrid,
      CategoryGrid,
      MonthNav,
      SankeyChart,
      DailySpendChart,
      CumulativeSpendChart,
      MDBModal,
      MDBModalBody,
      MDBModalHeader,
      MDBModalTitle,
      MDBCollapse,
      MDBBtn
    },
    props: {
      month: Number,
      year: Number
    },
    data() {
      return {
        expenses: reactive([]),
        savings: reactive([]),
        incomes: reactive([]),
        transfers: reactive([]),
        credit_card_payments: reactive([]),
        loading: true,
        selected_month: this.month - 1,
        selected_year: this.year,
        show_modal: false,
        show_raw_json: false,
        selected_transaction: reactive({}),
        detail_type_edit: '',
        transaction_types: [
          { value: 'expense', label: 'Expense' },
          { value: 'income', label: 'Income' },
          { value: 'investment', label: 'Savings / Investment' },
          { value: 'transfer', label: 'Transfer' },
          { value: 'cc_payment', label: 'Credit Card Payment' },
        ],
        categories: reactive([]),
        display_categories: reactive([]),
        selected_category: reactive({}),
        monthDataCache: new Map(),
        cacheVersion: 0,
        collapseCategories: true,
        collapseExpenses: true,
        collapseTransfers: true,
        collapseIncome: false,
        collapseSaving: false,
        collapseCcPayments: false,
        collapseCumulative: true,
        show_charts_modal: false,
        collapseTagBrowser: true,
        selected_tag: null,
        show_tag_modal: false,
        bulkTagging: false,
        bulkTagSelections: {},
      };
    },
    computed: {
      // Transfers that carry a real budget category count as spending everywhere.
      categorizedTransfers() {
        return this.transfers.filter(t => t.v3_category !== 'other');
      },
      // Combined array used by charts and projected-spend — same rule as
      // calculateCategoryTotals so all views stay consistent.
      expensesForCharts() {
        return [...this.expenses, ...this.categorizedTransfers];
      },

      totalIncome() {
        return this.incomes.reduce((s, t) => s + Math.abs(t.v3_amount || 0), 0);
      },
      totalExpenses() {
        return this.expensesForCharts.reduce((s, t) => s + Math.abs(t.v3_amount || 0), 0);
      },
      totalSavings() {
        return this.savings.reduce((s, t) => s + Math.abs(t.v3_amount || 0), 0);
      },
      totalTransfers() {
        return this.transfers.reduce((s, t) => s + Math.abs(t.v3_amount || 0), 0);
      },
      totalCcPayments() {
        return this.credit_card_payments.reduce((s, t) => s + Math.abs(t.v3_amount || 0), 0);
      },
      netAmount() {
        return this.totalIncome - this.totalExpenses - this.totalSavings;
      },

      // ── Savings rate (Feature 3) ─────────────────────────────────────
      savingsRate() {
        if (this.totalIncome === 0) return null;
        return Math.round(((this.totalIncome - this.totalExpenses) / this.totalIncome) * 100);
      },
      savingsRateClass() {
        const r = this.savingsRate;
        if (r === null) return '';
        if (r >= 20) return 'sr-great';
        if (r >= 10) return 'sr-ok';
        if (r >= 0)  return 'sr-low';
        return 'sr-neg';
      },
      savingsRateBarWidth() {
        const r = this.savingsRate;
        if (r === null) return '0%';
        return Math.max(0, Math.min(100, r)) + '%';
      },

      // ── Projected end-of-month spend (Feature 2) ────────────────────
      projectedExpenses() {
        const now = new Date();
        const isCurrentMonth =
          this.selected_month === now.getMonth() &&
          this.selected_year  === now.getFullYear();
        if (!isCurrentMonth || this.totalExpenses === 0) return null;

        const dayOfMonth  = now.getDate();
        if (dayOfMonth < 4) return null; // too early for a meaningful projection

        const daysInMonth = new Date(this.selected_year, this.selected_month + 1, 0).getDate();
        const dailyRate   = this.totalExpenses / dayOfMonth;
        const projected   = Math.round(dailyRate * daysInMonth);
        const overBudget  = this.totalBudget > 0 && projected > this.totalBudget;

        return { amount: projected, overBudget, daysLeft: daysInMonth - dayOfMonth };
      },
      totalBudget() {
        return (this.categories || []).reduce((s, c) => s + (c.budgeted || 0), 0);
      },

      // ── Tags ────────────────────────────────────────────────────────
      allTransactions() {
        return [
          ...this.expenses,
          ...this.incomes,
          ...this.transfers,
          ...this.savings,
          ...this.credit_card_payments,
        ].filter(t => !t.v3_deleted);
      },
      tagSummary() {
        const map = {};
        this.allTransactions.forEach(t => {
          (t.v3_tags || []).forEach(tag => {
            if (!map[tag]) map[tag] = { count: 0, total: 0, transactions: [] };
            map[tag].count++;
            map[tag].total += Math.abs(t.v3_amount || 0);
            map[tag].transactions.push(t);
          });
        });
        return Object.entries(map)
          .sort(([, a], [, b]) => b.total - a.total)
          .map(([tag, d]) => ({ tag, ...d }));
      },
      selectedTagTransactions() {
        if (!this.selected_tag) return [];
        return this.allTransactions
          .filter(t => (t.v3_tags || []).includes(this.selected_tag))
          .sort((a, b) => new Date(b.v3_date) - new Date(a.v3_date));
      },
      selectedTagTotal() {
        return this.selectedTagTransactions.reduce((s, t) => s + Math.abs(t.v3_amount || 0), 0);
      },

      // ── Daily spend chart previous-month data ────────────────────────
      prevMonthData() {
        void this.cacheVersion;
        const m      = this.selected_month + 1;
        const prev   = this.getPreviousMonth(m, this.selected_year);
        const cached = this.getCachedData(prev.month, prev.year);
        return {
          month:    prev.month - 1,
          year:     prev.year,
          expenses: [
            ...(cached?.expenses  || []),
            ...(cached?.transfers || []).filter(t => t.v3_category !== 'other'),
          ],
        };
      },
      prevPrevMonthData() {
        void this.cacheVersion;
        const m      = this.selected_month + 1;
        const prev   = this.getPreviousMonth(m, this.selected_year);
        const prev2  = this.getPreviousMonth(prev.month, prev.year);
        const cached = this.getCachedData(prev2.month, prev2.year);
        return {
          month:    prev2.month - 1,
          year:     prev2.year,
          expenses: [
            ...(cached?.expenses  || []),
            ...(cached?.transfers || []).filter(t => t.v3_category !== 'other'),
          ],
        };
      },

      // ── Month-over-month category spending (Feature 1) ──────────────
      prevMonthCategorySpending() {
        void this.cacheVersion;
        const m    = this.selected_month + 1;
        const y    = this.selected_year;
        const prev = this.getPreviousMonth(m, y);
        const cached = this.getCachedData(prev.month, prev.year);
        if (!cached) return {};
        const totals = {};
        const items = [
          ...cached.expenses,
          ...(cached.transfers || []).filter(t => t.v3_category !== 'other'),
        ];
        items.forEach(t => {
          if (!t.v3_deleted) {
            totals[t.v3_category] = (totals[t.v3_category] || 0) + Math.abs(t.v3_amount || 0);
          }
        });
        return totals;
      },
    },
    methods: {
      // ── Formatting helpers ────────────────────────────────────────────
      fmtCurrency(val) {
        return '$' + Math.round(Math.abs(val || 0)).toLocaleString();
      },
      fmtCurrencyFull(val) {
        return '$' + Math.abs(val || 0).toFixed(2);
      },
      categoryIconFor(name) {
        return CATEGORY_ICONS[name] || 'fa-tag';
      },
      categoryDisplayName(name) {
        return this.display_categories.find(c => c.value === name)?.text || name || '';
      },
      formatDetailDate(dateStr) {
        if (!dateStr) return '';
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
      },
      typeColor(type) {
        return TYPE_COLORS[type] || '#6b7280';
      },
      typeLabel(type) {
        return this.transaction_types.find(t => t.value === type)?.label || type || '';
      },

      // ── Detail modal ─────────────────────────────────────────────────
      showDetailFor(transaction) {
        if (!transaction?.date_transaction_id) return;
        this.show_tag_modal = false; // close tag modal if open
        const sorted = Object.fromEntries(Object.entries(transaction).sort((a, b) => a[0].localeCompare(b[0])));
        this.selected_transaction = sorted;
        this.detail_type_edit = transaction.v3_type;
        this.show_raw_json = false;
        this.show_modal = true;
      },
      closeModal() {
        this.show_modal = false;
      },
      confirmTypeChange() {
        const oldType = this.selected_transaction.v3_type;
        const newType = this.detail_type_edit;
        if (oldType === newType) return;

        const id = this.selected_transaction.date_transaction_id;
        const without = (arr) => arr.filter(t => t.date_transaction_id !== id);

        if (oldType === 'expense') this.expenses = without(this.expenses);
        else if (oldType === 'income') this.incomes = without(this.incomes);
        else if (oldType === 'investment') this.savings = without(this.savings);
        else if (oldType === 'transfer') this.transfers = without(this.transfers);
        else if (oldType === 'cc_payment') this.credit_card_payments = without(this.credit_card_payments);

        this.selected_transaction.v3_type = newType;

        if (newType === 'expense') this.expenses.push(this.selected_transaction);
        else if (newType === 'income') this.incomes.push(this.selected_transaction);
        else if (newType === 'investment') this.savings.push(this.selected_transaction);
        else if (newType === 'transfer') this.transfers.push(this.selected_transaction);
        else if (newType === 'cc_payment') this.credit_card_payments.push(this.selected_transaction);

        patch('transactions', { date_transaction_id: id, v3_type: newType })
          .catch(err => { console.error('Failed to update type', err); alert('Failed to update transaction type.'); });

        this.calculateCategoryTotals();
        this.show_modal = false;
      },

      openTagModal(tag) {
        this.show_modal = false;
        this.bulkTagging = false;
        this.bulkTagSelections = {};
        this.selected_tag = tag;
        this.show_tag_modal = true;
      },
      enterBulkTagMode() {
        const selections = {};
        this.allTransactions.forEach(t => {
          selections[t.date_transaction_id] = (t.v3_tags || []).includes(this.selected_tag);
        });
        this.bulkTagSelections = selections;
        this.bulkTagging = true;
      },
      exitBulkTagMode() {
        this.bulkTagging = false;
        this.bulkTagSelections = {};
      },
      saveBulkTags() {
        let store;
        try { store = JSON.parse(localStorage.getItem('txn_tags') || '{}'); } catch (e) { store = {}; }
        const changed = [];
        this.allTransactions.forEach(t => {
          const id = t.date_transaction_id;
          const wasTagged = (t.v3_tags || []).includes(this.selected_tag);
          const nowTagged = !!this.bulkTagSelections[id];
          if (wasTagged === nowTagged) return;
          if (nowTagged) {
            if (!Array.isArray(t.v3_tags)) t.v3_tags = [];
            if (!t.v3_tags.includes(this.selected_tag)) t.v3_tags.push(this.selected_tag);
          } else {
            t.v3_tags = (t.v3_tags || []).filter(tag => tag !== this.selected_tag);
          }
          store[id] = t.v3_tags;
          changed.push(t);
        });
        if (changed.length) {
          try { localStorage.setItem('txn_tags', JSON.stringify(store)); } catch (e) { /* ignore */ }
          changed.forEach(t => patch('transactions', t).catch(() => {}));
        }
        this.exitBulkTagMode();
      },

      // ── Selection ─────────────────────────────────────────────────────
      setSelectedTransaction(transaction) {
        this.selected_transaction = transaction;
      },
      setSelectedCategory(category) {
        this.selected_category = category;
      },

      // ── Cache helpers ─────────────────────────────────────────────────
      mergeLocalTags(arr) {
        if (!arr) return;
        try {
          const store = JSON.parse(localStorage.getItem('txn_tags') || '{}');
          arr.forEach(t => {
            const local = store[t.date_transaction_id];
            if (local !== undefined && !(t.v3_tags || []).length) {
              t.v3_tags = local;
            }
          });
        } catch (e) { /* ignore localStorage errors */ }
      },
      getCacheKey(month, year) {
        return `${year}-${String(month).padStart(2, '0')}`;
      },
      getCachedData(month, year) {
        return this.monthDataCache.get(this.getCacheKey(month, year));
      },
      setCachedData(month, year, data) {
        this.monthDataCache.set(this.getCacheKey(month, year), data);
        this.cacheVersion++; // nudge computed properties that read the cache
      },
      getPreviousMonth(month, year) {
        return month === 1 ? { month: 12, year: year - 1 } : { month: month - 1, year };
      },
      preloadPreviousMonths() {
        const currentMonth = this.selected_month + 1;
        const currentYear = this.selected_year;
        const prev1 = this.getPreviousMonth(currentMonth, currentYear);
        const prev2 = this.getPreviousMonth(prev1.month, prev1.year);
        const toLoad = [prev1, prev2].filter(p => !this.getCachedData(p.month, p.year));
        if (toLoad.length) {
          Promise.all(toLoad.map(p => this.preloadMonthData(p.month, p.year))).catch(() => {});
        }
      },
      async preloadMonthData(month, year) {
        if (this.getCachedData(month, year)) return;
        try {
          const mm = String(month).padStart(2, '0');
          const result = await get(`transactions?month=${mm}&year=${year}`);
          if (result.status === 401) {
            localStorage.removeItem('token');
            this.$router.push('/login');
            return;
          }
          const data = await result.json();
          const sorted = this.sortTransactions(data.Items);
          const processed = this.processTransactionData(sorted);
          ['expenses', 'incomes', 'savings', 'credit_card_payments', 'transfers'].forEach(k => {
            this.mergeLocalTags(processed[k]);
          });
          this.setCachedData(month, year, processed);
        } catch (err) {
          console.error(`Failed to preload ${month}/${year}:`, err);
        }
      },

      // ── Data processing ───────────────────────────────────────────────
      processTransactionData(transactions) {
        const expenses = [], incomes = [], savings = [], credit_card_payments = [], transfers = [];
        const debug = localStorage.getItem('debug') === 'true';
        transactions?.forEach(t => {
          if (!debug && t.v3_deleted) return;
          if (!('v3_type' in t)) return;
          if (t.v3_type === 'investment') savings.push(t);
          else if (t.v3_type.includes('expense')) expenses.push(t);
          else if (t.v3_type.includes('income')) incomes.push(t);
          else if (t.v3_type.includes('cc_payment')) credit_card_payments.push(t);
          else if (t.v3_type.includes('transfer')) transfers.push(t);
        });
        return { expenses, incomes, savings, credit_card_payments, transfers };
      },
      transactionEdited() {
        this.calculateCategoryTotals();
        this.removeDeleted();
      },
      removeDeleted() {
        if (localStorage.getItem('debug') !== 'true') {
          const alive = arr => arr.filter(e => e.v3_deleted !== true);
          this.expenses = alive(this.expenses);
          this.incomes = alive(this.incomes);
          this.transfers = alive(this.transfers);
          this.savings = alive(this.savings);
          this.credit_card_payments = alive(this.credit_card_payments);
        }
      },
      calculateCategoryTotals() {
        const totals = {};
        [...this.expenses, ...this.transfers.filter(t => t.v3_category !== 'other')].forEach(t => {
          totals[t.v3_category] = (totals[t.v3_category] || 0) + Number(t.v3_amount);
        });
        this.categories = (this.categories || []).map(c => ({
          ...c,
          spent: Number(totals[c.name] || 0),
        }));
      },
      getCategories() {
        this.loading = true;
        return get('categories')
          .then(r => {
            if (r.status === 401) { localStorage.removeItem('token'); this.$router.push('/login'); throw new Error('Unauthorized'); }
            return r.json();
          })
          .then(data => {
            this.categories = data;
            this.display_categories = (data || []).map(c => ({ text: c.display_name, value: c.name }));
            this.getTransactions();
          });
      },
      getTransactions() {
        this.loading = true;
        const m = this.selected_month + 1;
        const y = this.selected_year;
        const cached = this.getCachedData(m, y);
        if (cached) {
          this.expenses = cached.expenses;
          this.incomes = cached.incomes;
          this.savings = cached.savings;
          this.credit_card_payments = cached.credit_card_payments;
          this.transfers = cached.transfers;
          this.calculateCategoryTotals();
          this.loading = false;
          this.preloadPreviousMonths();
          return;
        }
        const mm = String(m).padStart(2, '0');
        get(`transactions?month=${mm}&year=${y}`)
          .then(r => {
            if (r.status === 401) { localStorage.removeItem('token'); this.$router.push('/login'); throw new Error('Unauthorized'); }
            return r.json();
          })
          .then(d => {
            const sorted = this.sortTransactions(d.Items);
            const processed = this.processTransactionData(sorted);
            ['expenses', 'incomes', 'savings', 'credit_card_payments', 'transfers'].forEach(k => {
              this.mergeLocalTags(processed[k]);
            });
            this.expenses = processed.expenses;
            this.incomes = processed.incomes;
            this.savings = processed.savings;
            this.credit_card_payments = processed.credit_card_payments;
            this.transfers = processed.transfers;
            this.setCachedData(m, y, processed);
            this.calculateCategoryTotals();
            this.loading = false;
            this.preloadPreviousMonths();
          })
          .catch(err => { alert(err); this.loading = false; });
      },
      sortTransactions(rowData) {
        if (!rowData) return rowData;
        return rowData.sort((a, b) => {
          const da = Date.parse(a.authorized_date || a.date);
          const db = Date.parse(b.authorized_date || b.date);
          return da < db ? -1 : da > db ? 1 : 0;
        });
      },
      onFileChanged(event) {
        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onload = (e) => {
          const lines = e.target.result.split(/\r\n|\n/).slice(1);
          const transactions = [];
          lines.forEach(line => {
            const fields = line.split(',');
            if (fields.length > 1) {
              const amount = parseFloat(fields[2].replace(/"/g, '')) * -1;
              const date = new Date(fields[0].replace(/"/g, ''));
              transactions.push({
                v3_date: date.toISOString().split('T')[0],
                v3_name: fields[1].replace(/"/g, ''),
                v3_amount: amount,
                v3_type: amount > 0 ? 'expense' : 'cc_payment',
                v3_category: 'other',
                v3_account_name: 'Penfed - Power Cash Rewards',
              });
            }
          });
          post('transactions', transactions)
            .then(r => {
              if (r.status === 401) { localStorage.removeItem('token'); this.$router.push('/login'); throw new Error('Unauthorized'); }
              return r.json();
            })
            .then(() => this.getTransactions());
        };
        reader.readAsText(file);
      },
    },
    created() {
      checkLogin();
      this.getCategories();
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.selected_transaction?.date_transaction_id) {
          this.showDetailFor(this.selected_transaction);
          e.stopPropagation();
        }
      });
    },
    watch: {
      expenses() { this.calculateCategoryTotals(); },
      month(v) { this.selected_month = v - 1; this.getTransactions(); },
      year(v) { this.selected_year = v; this.getTransactions(); },
    }
  });
  </script>

<template>
  <div class="month-view">

    <!-- ── Transaction Detail Modal ─────────────────────────────── -->
    <MDBModal
      id="detailModal"
      tabindex="-1"
      labelledby="detailModalLabel"
      v-model="show_modal"
      size="lg"
      centered
    >
      <!-- Colored header band -->
      <div
        class="detail-header"
        :style="{ background: typeColor(selected_transaction.v3_type) + '18', borderTop: '4px solid ' + typeColor(selected_transaction.v3_type) }"
      >
        <div class="detail-header-inner">
          <span class="detail-cat-badge" :style="{ background: typeColor(selected_transaction.v3_type) }">
            <i :class="['fas', categoryIconFor(selected_transaction.v3_category)]"></i>
          </span>
          <div class="detail-title-block">
            <h6 class="detail-txn-name">{{ selected_transaction.v3_name }}</h6>
            <span class="detail-txn-date">{{ formatDetailDate(selected_transaction.v3_date) }}</span>
          </div>
          <button class="btn-close detail-close" @click="closeModal"></button>
        </div>
        <div class="detail-amount-row">
          <span
            class="detail-amount"
            :class="(selected_transaction.v3_amount || 0) > 0 ? 'amount-debit' : 'amount-credit'"
          >
            {{ fmtCurrencyFull(selected_transaction.v3_amount) }}
          </span>
          <span class="detail-type-badge" :style="{ color: typeColor(selected_transaction.v3_type) }">
            {{ typeLabel(selected_transaction.v3_type) }}
          </span>
        </div>
      </div>

      <MDBModalBody class="p-0">
        <div class="detail-fields">
          <div class="detail-row">
            <span class="detail-label">Account</span>
            <span class="detail-val">{{ selected_transaction.v3_account_name }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Category</span>
            <span class="detail-val">
              <i :class="['fas', categoryIconFor(selected_transaction.v3_category)]" style="margin-right:6px; color:#6b7280"></i>
              {{ categoryDisplayName(selected_transaction.v3_category) }}
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Status</span>
            <span class="detail-val">
              <span v-if="selected_transaction.pending" class="status-chip pending">Pending</span>
              <span v-else class="status-chip posted">Posted</span>
              <span v-if="selected_transaction.v3_deleted" class="status-chip deleted">Deleted</span>
            </span>
          </div>

          <!-- Type change — the "rare interaction" -->
          <div class="detail-row type-change-row">
            <span class="detail-label">Type</span>
            <div class="detail-val type-val">
              <select v-model="detail_type_edit" class="type-select">
                <option v-for="t in transaction_types" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
              <button
                v-if="detail_type_edit !== selected_transaction.v3_type"
                class="type-save-btn"
                @click="confirmTypeChange"
              >
                Save
              </button>
            </div>
          </div>

          <!-- Tags (read-only; edit via the row modal) -->
          <div class="detail-row" v-if="(selected_transaction.v3_tags || []).length">
            <span class="detail-label">Tags</span>
            <div class="detail-val" style="gap:5px; flex-wrap:wrap;">
              <span
                v-for="tag in selected_transaction.v3_tags"
                :key="tag"
                class="detail-tag"
                @click="openTagModal(tag)"
              >{{ tag }}</span>
            </div>
          </div>

          <!-- Note (read-only) -->
          <div class="detail-row" v-if="selected_transaction.v3_notes">
            <span class="detail-label">Note</span>
            <span class="detail-val" style="white-space:pre-wrap; color:#374151">{{ selected_transaction.v3_notes }}</span>
          </div>

          <!-- Raw JSON -->
          <div class="detail-row detail-row--json">
            <button class="json-toggle" @click="show_raw_json = !show_raw_json">
              {{ show_raw_json ? '▾' : '▸' }} Raw JSON
            </button>
            <pre v-if="show_raw_json" class="json-blob">{{ JSON.stringify(selected_transaction, null, 2) }}</pre>
          </div>
        </div>
      </MDBModalBody>

      <div class="detail-footer">
        <button class="btn btn-sm btn-outline-secondary" @click="closeModal">Close</button>
      </div>
    </MDBModal>

    <!-- ── Month navigation ─────────────────────────────────────── -->
    <MonthNav
      :selected_month="selected_month"
      :selected_year="selected_year"
    />

    <!-- ── Summary widget ───────────────────────────────────────── -->
    <div class="summary-widget" v-if="!loading && totalIncome > 0">

      <!-- Savings rate hero -->
      <div class="sw-rate-row">
        <div class="sw-rate-left">
          <span class="sw-rate-label">Savings Rate</span>
          <span class="sw-rate-pct" :class="savingsRateClass">
            {{ savingsRate !== null ? savingsRate + '%' : '—' }}
          </span>
        </div>
        <span v-if="savingsRate !== null && savingsRate >= 20" class="sw-badge sw-badge--good">On track ✓</span>
        <span v-else-if="savingsRate !== null && savingsRate < 0"  class="sw-badge sw-badge--bad">Over income</span>
        <span v-else-if="savingsRate !== null"                     class="sw-badge sw-badge--warn">Below 20% goal</span>
      </div>

      <!-- Progress bar — fill = savings rate, marker = 20% goal -->
      <div class="sw-bar-track">
        <div class="sw-bar-fill" :class="savingsRateClass" :style="{ width: savingsRateBarWidth }"></div>
        <div class="sw-bar-goal" title="20% savings goal"></div>
      </div>
      <p class="sw-bar-caption">
        <template v-if="(savingsRate || 0) >= 0">
          {{ fmtCurrency(totalIncome - totalExpenses) }} saved of {{ fmtCurrency(totalIncome) }} income
        </template>
        <template v-else>
          Spending {{ fmtCurrency(totalExpenses - totalIncome) }} more than income
        </template>
      </p>

      <!-- Key stats row -->
      <div class="sw-stats">
        <div class="sw-stat">
          <span class="sw-stat-label">Income</span>
          <span class="sw-stat-val sw-income">{{ fmtCurrency(totalIncome) }}</span>
        </div>
        <div class="sw-divider"></div>
        <div class="sw-stat">
          <span class="sw-stat-label">Spent</span>
          <span class="sw-stat-val sw-expense">{{ fmtCurrency(totalExpenses) }}</span>
        </div>
        <div class="sw-divider"></div>
        <div class="sw-stat" v-if="projectedExpenses">
          <span class="sw-stat-label">On pace for</span>
          <span class="sw-stat-val" :class="projectedExpenses.overBudget ? 'sw-warn' : 'sw-ok'">
            {{ fmtCurrency(projectedExpenses.amount) }}
            <i v-if="projectedExpenses.overBudget" class="fas fa-exclamation-triangle" style="font-size:11px"></i>
          </span>
        </div>
        <div class="sw-stat" v-else>
          <span class="sw-stat-label">Invested</span>
          <span class="sw-stat-val sw-savings">{{ fmtCurrency(totalSavings) }}</span>
        </div>
      </div>
    </div>

    <!-- ── Cumulative Spend Tracker ──────────────────────────────── -->
    <div class="mv-section">
      <button class="section-toggle" @click="collapseCumulative = !collapseCumulative">
        <i :class="['fas', collapseCumulative ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
        <span class="section-name">Spend Tracker</span>
      </button>
      <!-- v-show avoids MDBCollapse height-animation racing with canvas init -->
      <div v-show="collapseCumulative" class="chart-container">
        <CumulativeSpendChart
          :expenses="expensesForCharts"
          :selectedYear="selected_year"
          :selectedMonth="selected_month"
          :prevMonthExpenses="prevMonthData.expenses"
          :prevMonthYear="prevMonthData.year"
          :prevMonth="prevMonthData.month"
          :prevPrevMonthExpenses="prevPrevMonthData.expenses"
          :prevPrevMonthYear="prevPrevMonthData.year"
          :prevPrevMonth="prevPrevMonthData.month"
          :totalBudget="totalBudget"
        />
      </div>
    </div>

    <!-- ── Charts popup trigger ──────────────────────────────────── -->
    <div class="charts-link-row">
      <button class="charts-link-btn" @click="show_charts_modal = true">
        <i class="fas fa-chart-area"></i>
        Daily spending &amp; cash flow
        <i class="fas fa-arrow-right charts-link-arrow"></i>
      </button>
    </div>

    <!-- ── Charts modal (Daily + Sankey) ────────────────────────── -->
    <MDBModal v-model="show_charts_modal" size="xl">
      <MDBModalHeader>
        <MDBModalTitle>Charts</MDBModalTitle>
      </MDBModalHeader>
      <MDBModalBody class="charts-modal-body">
        <!-- Only mount chart components when modal is open so canvas has valid dimensions -->
        <template v-if="show_charts_modal">
          <p class="charts-modal-label">Daily Spending</p>
          <div class="charts-modal-chart">
            <DailySpendChart
              :expenses="expensesForCharts"
              :selectedYear="selected_year"
              :selectedMonth="selected_month"
              :prevMonthExpenses="prevMonthData.expenses"
              :prevMonthYear="prevMonthData.year"
              :prevMonth="prevMonthData.month"
              :prevPrevMonthExpenses="prevPrevMonthData.expenses"
              :prevPrevMonthYear="prevPrevMonthData.year"
              :prevPrevMonth="prevPrevMonthData.month"
            />
          </div>
          <p class="charts-modal-label" style="margin-top:28px">Cash Flow</p>
          <div class="charts-modal-chart charts-modal-chart--sankey">
            <SankeyChart
              :expenses="expenses"
              :incomes="incomes"
              :transfers="transfers"
              :savings="savings"
              :categories="categories"
            />
          </div>
        </template>
      </MDBModalBody>
    </MDBModal>

    <!-- ── Tag drill-down modal ─────────────────────────────────── -->
    <MDBModal v-model="show_tag_modal" centered size="lg">
      <MDBModalHeader>
        <MDBModalTitle>
          <i class="fas fa-tag" style="margin-right:8px; color:#4338ca"></i>{{ selected_tag }}
        </MDBModalTitle>
      </MDBModalHeader>
      <MDBModalBody class="p-0">

        <!-- Normal view: tagged transactions only -->
        <template v-if="!bulkTagging">
          <div class="tag-modal-header">
            <span class="tag-modal-count">{{ selectedTagTransactions.length }} transactions</span>
            <span class="tag-modal-total">{{ fmtCurrency(selectedTagTotal) }}</span>
          </div>
          <div class="tag-modal-list">
            <div
              v-for="txn in selectedTagTransactions"
              :key="txn.date_transaction_id"
              class="tag-modal-row"
            >
              <span class="tag-modal-icon">
                <i :class="['fas', categoryIconFor(txn.v3_category)]"></i>
              </span>
              <div class="tag-modal-body">
                <span class="tag-modal-name">{{ txn.v3_name }}</span>
                <span class="tag-modal-meta">
                  {{ categoryDisplayName(txn.v3_category) }} · {{ formatDetailDate(txn.v3_date) }}
                </span>
              </div>
              <span class="tag-modal-amount">${{ Math.abs(txn.v3_amount || 0).toFixed(2) }}</span>
            </div>
          </div>
        </template>

        <!-- Bulk-tag view: all transactions with checkboxes -->
        <template v-else>
          <div class="tag-modal-header">
            <span class="tag-modal-count">Check transactions to add / remove this tag</span>
          </div>
          <div class="tag-modal-list">
            <label
              v-for="txn in allTransactions.slice().sort((a,b) => new Date(b.v3_date) - new Date(a.v3_date))"
              :key="txn.date_transaction_id"
              class="tag-modal-row tag-bulk-row"
            >
              <input
                type="checkbox"
                class="tag-bulk-check"
                :checked="!!bulkTagSelections[txn.date_transaction_id]"
                @change="bulkTagSelections[txn.date_transaction_id] = $event.target.checked"
              />
              <span class="tag-modal-icon">
                <i :class="['fas', categoryIconFor(txn.v3_category)]"></i>
              </span>
              <div class="tag-modal-body">
                <span class="tag-modal-name">{{ txn.v3_name }}</span>
                <span class="tag-modal-meta">
                  {{ categoryDisplayName(txn.v3_category) }} · {{ formatDetailDate(txn.v3_date) }}
                </span>
              </div>
              <span class="tag-modal-amount">${{ Math.abs(txn.v3_amount || 0).toFixed(2) }}</span>
            </label>
          </div>
        </template>

      </MDBModalBody>
      <div class="detail-footer">
        <template v-if="!bulkTagging">
          <button class="btn btn-sm btn-outline-secondary" @click="show_tag_modal = false">Close</button>
          <button class="btn btn-sm btn-outline-primary" style="margin-left:8px" @click="enterBulkTagMode">Add to transactions</button>
        </template>
        <template v-else>
          <button class="btn btn-sm btn-outline-secondary" @click="exitBulkTagMode">Cancel</button>
          <button class="btn btn-sm btn-primary" style="margin-left:8px" @click="saveBulkTags">Save</button>
        </template>
      </div>
    </MDBModal>

    <!-- ── Tags ─────────────────────────────────────────────────── -->
    <div class="mv-section" v-if="tagSummary.length > 0">
      <button class="section-toggle" @click="collapseTagBrowser = !collapseTagBrowser">
        <i :class="['fas', collapseTagBrowser ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
        <span class="section-name">Tags</span>
        <span class="section-total">{{ tagSummary.length }}</span>
      </button>
      <MDBCollapse v-model="collapseTagBrowser">
        <div class="tag-browser">
          <div
            v-for="item in tagSummary"
            :key="item.tag"
            class="tag-browser-chip"
            @click="openTagModal(item.tag)"
          >
            <span class="tbc-name">{{ item.tag }}</span>
            <span class="tbc-meta">{{ fmtCurrency(item.total) }} · {{ item.count }}</span>
          </div>
        </div>
      </MDBCollapse>
    </div>

    <!-- ── Spending Categories ──────────────────────────────────── -->
    <div class="mv-section">
      <button class="section-toggle" @click="collapseCategories = !collapseCategories">
        <i :class="['fas', collapseCategories ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
        <span class="section-name">Categories</span>
        <span class="section-total">{{ fmtCurrency(totalExpenses) }}</span>
      </button>
      <MDBCollapse v-model="collapseCategories">
        <CategoryGrid
          :data="categories"
          :loading="loading"
          :prev_spending="prevMonthCategorySpending"
          @category-selected="setSelectedCategory"
        />
      </MDBCollapse>
    </div>

    <!-- ── Spending ──────────────────────────────────────────────── -->
    <div class="mv-section">
      <button class="section-toggle" @click="collapseExpenses = !collapseExpenses">
        <i :class="['fas', collapseExpenses ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
        <span class="section-name">Spending</span>
        <span class="section-total expense-total">{{ fmtCurrency(totalExpenses) }}</span>
      </button>
      <MDBCollapse v-model="collapseExpenses">
        <BudgetItemGrid
          heading="spending"
          type="expense"
          :data="expenses"
          :loading="loading"
          :expense_categories="display_categories"
          :selected_category="selected_category"
          :year="selected_year"
          :month="selected_month"
          @transaction-edited="transactionEdited"
          @transaction-deleted="transactionEdited"
          @transaction-added="transactionEdited"
          @transaction-selected="setSelectedTransaction"
          @show-detail="showDetailFor"
          @tag-clicked="openTagModal"
        />
      </MDBCollapse>
    </div>

    <!-- ── Transfers ────────────────────────────────────────────── -->
    <div class="mv-section">
      <button class="section-toggle" @click="collapseTransfers = !collapseTransfers">
        <i :class="['fas', collapseTransfers ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
        <span class="section-name">Transfers</span>
        <span class="section-total">{{ fmtCurrency(totalTransfers) }}</span>
      </button>
      <MDBCollapse v-model="collapseTransfers">
        <BudgetItemGrid
          heading="transfers"
          type="transfers"
          :data="transfers"
          :loading="loading"
          :expense_categories="display_categories"
          :selected_category="selected_category"
          :year="selected_year"
          :month="selected_month"
          @transaction-deleted="transactionEdited"
          @transaction-edited="transactionEdited"
          @transaction-added="transactionEdited"
          @transaction-selected="setSelectedTransaction"
          @show-detail="showDetailFor"
          @tag-clicked="openTagModal"
        />
      </MDBCollapse>
    </div>

    <!-- ── Income ───────────────────────────────────────────────── -->
    <div class="mv-section">
      <button class="section-toggle" @click="collapseIncome = !collapseIncome">
        <i :class="['fas', collapseIncome ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
        <span class="section-name">Income</span>
        <span class="section-total income-total">{{ fmtCurrency(totalIncome) }}</span>
      </button>
      <MDBCollapse v-model="collapseIncome">
        <BudgetItemGrid
          heading="income"
          type="income"
          :data="incomes"
          :loading="loading"
          :selected_category="selected_category"
          :year="selected_year"
          :month="selected_month"
          @transaction-deleted="transactionEdited"
          @transaction-edited="transactionEdited"
          @transaction-added="transactionEdited"
          @transaction-selected="setSelectedTransaction"
          @show-detail="showDetailFor"
          @tag-clicked="openTagModal"
        />
      </MDBCollapse>
    </div>

    <!-- ── Savings / Investments ────────────────────────────────── -->
    <div class="mv-section">
      <button class="section-toggle" @click="collapseSaving = !collapseSaving">
        <i :class="['fas', collapseSaving ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
        <span class="section-name">Savings</span>
        <span class="section-total savings-total">{{ fmtCurrency(totalSavings) }}</span>
      </button>
      <MDBCollapse v-model="collapseSaving">
        <BudgetItemGrid
          heading="saving"
          type="investment"
          :data="savings"
          :loading="loading"
          :selected_category="selected_category"
          :year="selected_year"
          :month="selected_month"
          @transaction-deleted="transactionEdited"
          @transaction-edited="transactionEdited"
          @transaction-added="transactionEdited"
          @transaction-selected="setSelectedTransaction"
          @show-detail="showDetailFor"
          @tag-clicked="openTagModal"
        />
      </MDBCollapse>
    </div>

    <!-- ── Credit Card Payments ──────────────────────────────────── -->
    <div class="mv-section">
      <button class="section-toggle" @click="collapseCcPayments = !collapseCcPayments">
        <i :class="['fas', collapseCcPayments ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
        <span class="section-name">CC Payments</span>
        <span class="section-total">{{ fmtCurrency(totalCcPayments) }}</span>
      </button>
      <MDBCollapse v-model="collapseCcPayments">
        <BudgetItemGrid
          heading="credit card payments"
          type="cc_payment"
          :data="credit_card_payments"
          :loading="loading"
          :selected_category="selected_category"
          :year="selected_year"
          :month="selected_month"
          @transaction-deleted="transactionEdited"
          @transaction-edited="transactionEdited"
          @transaction-added="transactionEdited"
          @transaction-selected="setSelectedTransaction"
          @show-detail="showDetailFor"
          @tag-clicked="openTagModal"
        />
      </MDBCollapse>
    </div>

    <!-- ── CSV import ───────────────────────────────────────────── -->
    <div class="csv-import">
      <input
        type="file"
        @change="onFileChanged($event)"
        accept="text/csv"
        capture
      />
    </div>

  </div>
</template>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────── */
.month-view {
  padding-bottom: 80px;
}

/* ── Summary widget ─────────────────────────────────────────────── */
.summary-widget {
  margin: 8px 16px 4px;
  padding: 16px 20px 12px;
  background: #fff;
  border: 1.5px solid #e8eaf0;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}

/* Rate row */
.sw-rate-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.sw-rate-left {
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.sw-rate-label {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.sw-rate-pct {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.5px;
}
.sw-rate-pct.sr-great { color: #10b981; }
.sw-rate-pct.sr-ok    { color: #f59e0b; }
.sw-rate-pct.sr-low   { color: #f97316; }
.sw-rate-pct.sr-neg   { color: #ef4444; }

/* Badges */
.sw-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 99px;
  flex-shrink: 0;
}
.sw-badge--good { background: #d1fae5; color: #065f46; }
.sw-badge--warn { background: #fef3c7; color: #92400e; }
.sw-badge--bad  { background: #fee2e2; color: #991b1b; }

/* Bar */
.sw-bar-track {
  position: relative;
  height: 8px;
  background: #f1f2f6;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
}
.sw-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}
.sw-bar-fill.sr-great { background: #10b981; }
.sw-bar-fill.sr-ok    { background: #f59e0b; }
.sw-bar-fill.sr-low   { background: #f97316; }
.sw-bar-fill.sr-neg   { background: #ef4444; width: 0 !important; }
/* 20% goal marker — fixed at 20% of bar width */
.sw-bar-goal {
  position: absolute;
  top: 0; bottom: 0;
  left: 20%;
  width: 2px;
  background: rgba(0,0,0,0.25);
  border-radius: 1px;
}
.sw-bar-caption {
  font-size: 12px;
  color: #9ca3af;
  margin: 0 0 12px;
}

/* Stats row */
.sw-stats {
  display: flex;
  align-items: center;
  border-top: 1px solid #f1f2f6;
  padding-top: 10px;
}
.sw-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.sw-divider {
  width: 1px;
  height: 28px;
  background: #e8eaf0;
}
.sw-stat-label {
  font-size: 10px;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 2px;
}
.sw-stat-val {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.sw-income  { color: #10b981; }
.sw-expense { color: #ef4444; }
.sw-savings { color: #8b5cf6; }
.sw-ok      { color: #10b981; }
.sw-warn    { color: #f97316; }

@media (max-width: 400px) {
  .sw-stat-val { font-size: 13px; }
  .sw-rate-pct { font-size: 24px; }
}

/* ── Section toggles ────────────────────────────────────────────── */
.mv-section {
  margin: 4px 0;
}
.section-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-align: left;
  transition: background 0.1s;
}
.section-toggle:hover {
  background: #f9fafb;
}
.section-toggle i {
  font-size: 11px;
  color: #9ca3af;
  width: 12px;
  flex-shrink: 0;
}
.section-name {
  flex: 1;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
}
.section-total {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.section-total.expense-total { color: #ef4444; }
.section-total.income-total { color: #10b981; }
.section-total.savings-total { color: #8b5cf6; }
.section-badge {
  font-size: 10px;
  background: #e0e7ff;
  color: #4f46e5;
  padding: 2px 7px;
  border-radius: 99px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* ── Shared chart card ──────────────────────────────────────────── */
.chart-container,
.sankey-container {
  margin: 0 16px 12px;
  padding: 16px;
  background: #fff;
  border: 1.5px solid #e8eaf0;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}

/* ── Detail modal ───────────────────────────────────────────────── */
.detail-header {
  padding: 16px 20px 12px;
  border-radius: 4px 4px 0 0;
}
.detail-header-inner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}
.detail-cat-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: #fff;
  font-size: 16px;
  flex-shrink: 0;
}
.detail-title-block {
  flex: 1;
}
.detail-txn-name {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}
.detail-txn-date {
  font-size: 12px;
  color: #6b7280;
}
.detail-close {
  margin-top: 4px;
}
.detail-amount-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.detail-amount {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.5px;
}
.amount-debit { color: #ef4444; }
.amount-credit { color: #10b981; }
.detail-type-badge {
  font-size: 12px;
  font-weight: 600;
}

.detail-fields {
  border-top: 1px solid #f3f4f6;
}
.detail-row {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #f3f4f6;
  gap: 12px;
}
.detail-label {
  width: 80px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.detail-val {
  flex: 1;
  font-size: 14px;
  color: #111827;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

/* Status chips */
.status-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 600;
}
.status-chip.posted { background: #d1fae5; color: #065f46; }
.status-chip.pending { background: #fef3c7; color: #92400e; }
.status-chip.deleted { background: #fee2e2; color: #991b1b; }

/* Type change row */
.type-change-row { flex-wrap: wrap; }
.type-val { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.type-select {
  padding: 5px 10px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  background: #fff;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
}
.type-select:focus { border-color: #4f8ef7; }
.type-save-btn {
  padding: 5px 14px;
  background: #4f8ef7;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.type-save-btn:hover { background: #3b7ef0; }

.detail-footer {
  padding: 12px 20px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: flex-end;
}
.detail-row--json {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.json-toggle {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.78rem;
  color: #9ca3af;
  cursor: pointer;
  letter-spacing: 0.02em;
}
.json-toggle:hover { color: #6b7280; }
.json-blob {
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.72rem;
  line-height: 1.5;
  color: #374151;
  overflow-x: auto;
  white-space: pre;
}

/* ── Tag browser section ────────────────────────────────────────── */
.tag-browser {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 20px 16px;
}
.tag-browser-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: #eef2ff;
  border: 1.5px solid #c7d2fe;
  border-radius: 99px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}
.tag-browser-chip:hover {
  background: #e0e7ff;
  border-color: #a5b4fc;
}
.tbc-name {
  font-size: 13px;
  font-weight: 600;
  color: #3730a3;
}
.tbc-meta {
  font-size: 11px;
  color: #6366f1;
}

/* ── Tag drill-down modal ───────────────────────────────────────── */
.tag-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #f3f4f6;
}
.tag-modal-count { font-size: 13px; color: #6b7280; }
.tag-modal-total { font-size: 16px; font-weight: 700; color: #111827; }

.tag-modal-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  border-bottom: 1px solid #f3f4f6;
}
.tag-modal-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
}
.tag-modal-body { flex: 1; min-width: 0; }
.tag-modal-name {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tag-modal-meta { font-size: 12px; color: #9ca3af; }
.tag-modal-amount { font-size: 14px; font-weight: 700; color: #111827; flex-shrink: 0; }

.tag-bulk-row { cursor: pointer; }
.tag-bulk-row:hover { background: #f9fafb; }
.tag-bulk-check {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: #4338ca;
  cursor: pointer;
}

/* ── Detail modal — tag + note ──────────────────────────────────── */
.detail-tag {
  display: inline-block;
  padding: 2px 10px;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 4px;
}
.detail-tag:hover { background: #c7d2fe; }

/* ── Charts link row ────────────────────────────────────────────── */
.charts-link-row {
  padding: 4px 16px 8px;
}
.charts-link-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background: #f9fafb;
  border: 1.5px dashed #d1d5db;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
}
.charts-link-btn:hover {
  background: #f0f5ff;
  border-color: #4f8ef7;
  color: #4f8ef7;
}
.charts-link-arrow {
  margin-left: auto;
  font-size: 11px;
}

/* ── Charts modal ───────────────────────────────────────────────── */
.charts-modal-body {
  padding: 20px;
}
.charts-modal-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  margin: 0 0 10px;
}
.charts-modal-chart {
  background: #fafafa;
  border: 1.5px solid #e8eaf0;
  border-radius: 12px;
  padding: 14px;
}
.charts-modal-chart--sankey {
  height: 400px;
}

/* ── CSV import ─────────────────────────────────────────────────── */
.csv-import {
  padding: 12px 20px;
}
</style>

<style>
/* Override MDBCollapse height behavior */
.collapse.show { height: auto !important; }
.collapse { overflow: visible !important; }
</style>
